/**
 * Take the GPS location out of a JPEG's EXIF block, byte by byte, with no
 * decode.
 *
 * Why (refute round 1 on #1401): two upload paths send a JPEG exactly as it
 * was picked, the quick skip for a small JPEG and the original sent after a
 * shrink runs out of time. A phone camera JPEG carries the GPS position it
 * was taken at, and the server does not strip it, so every family who could
 * see the photo could read where it was taken. The re-encoded path never had
 * this problem: a canvas JPEG carries no EXIF at all.
 *
 * What it does, in the first EXIF (APP1 "Exif") segment only:
 *   - removes the GPSInfo (0x8825) entry from IFD0, shifting the later
 *     entries down so the directory stays sorted and well formed;
 *   - zeroes the GPS directory itself and every value it points at, so the
 *     coordinates are gone from the bytes, not just unlinked;
 *   - drops any XMP (APP1 "http://ns.adobe.com/xap/1.0/") segment that
 *     mentions GPS, since XMP can carry the same coordinates as text.
 * Orientation (0x0112) and everything else in EXIF are left exactly as they
 * were, so a portrait still shows upright. The compressed image data is
 * never touched.
 *
 * FAILS SAFE: anything it does not understand returns null, which the caller
 * reads as "send the original unchanged". It only ever looks at the head of
 * the file (the caller passes the first bytes; EXIF must sit before the image
 * data and an APP1 segment is at most 64 KB).
 *
 * Imports nothing, so it is pinned by a plain Node test with a real EXIF
 * fixture (tests/jpeg-exif-scrub.test.mjs).
 */

const GPS_INFO_TAG = 0x8825;
const TYPE_SIZES: Record<number, number> = {
  1: 1, 2: 1, 3: 2, 4: 4, 5: 8, 6: 1, 7: 1, 8: 2, 9: 4, 10: 8, 11: 4, 12: 8,
};

function ascii(bytes: Uint8Array, start: number, length: number): string {
  let out = '';
  for (let i = 0; i < length && start + i < bytes.length; i += 1) out += String.fromCharCode(bytes[start + i]);
  return out;
}

/** Scrub GPS out of one EXIF TIFF block, in place. True when something was removed. */
function scrubTiff(tiff: Uint8Array): boolean {
  if (tiff.length < 8) throw new Error('short tiff');
  const order = ascii(tiff, 0, 2);
  if (order !== 'II' && order !== 'MM') throw new Error('byte order');
  const little = order === 'II';
  const view = new DataView(tiff.buffer, tiff.byteOffset, tiff.byteLength);
  const u16 = (at: number) => view.getUint16(at, little);
  const u32 = (at: number) => view.getUint32(at, little);
  const inside = (at: number, length: number) => at >= 0 && length >= 0 && at + length <= tiff.length;
  if (u16(2) !== 42) throw new Error('magic');
  const ifd0 = u32(4);
  if (!inside(ifd0, 2)) throw new Error('ifd0');
  const count = u16(ifd0);
  const entriesStart = ifd0 + 2;
  if (count === 0 || !inside(entriesStart, count * 12 + 4)) throw new Error('ifd0 entries');

  let gpsIndex = -1;
  for (let i = 0; i < count; i += 1) {
    if (u16(entriesStart + i * 12) === GPS_INFO_TAG) {
      gpsIndex = i;
      break;
    }
  }
  if (gpsIndex === -1) return false;
  const gpsEntry = entriesStart + gpsIndex * 12;
  const gpsOffset = u32(gpsEntry + 8);

  // Zero the GPS directory and every value it points at. Bounds are checked
  // before anything is written, so a malformed block changes nothing.
  const wipes: Array<[number, number]> = [];
  if (inside(gpsOffset, 2)) {
    const gpsCount = u16(gpsOffset);
    if (!inside(gpsOffset + 2, gpsCount * 12 + 4)) throw new Error('gps entries');
    for (let i = 0; i < gpsCount; i += 1) {
      const entry = gpsOffset + 2 + i * 12;
      const size = (TYPE_SIZES[u16(entry + 2)] ?? 0) * u32(entry + 4);
      if (size > 4) {
        const at = u32(entry + 8);
        if (!inside(at, size)) throw new Error('gps value');
        wipes.push([at, size]);
      }
    }
    wipes.push([gpsOffset, 2 + gpsCount * 12 + 4]);
  }

  // Drop the GPSInfo entry: shift the later entries (and the next-IFD
  // pointer after them) down by one entry, then zero the freed tail.
  const tailStart = gpsEntry + 12;
  const tailEnd = entriesStart + count * 12 + 4;
  tiff.copyWithin(gpsEntry, tailStart, tailEnd);
  tiff.fill(0, tailEnd - 12, tailEnd);
  view.setUint16(ifd0, count - 1, little);
  for (const [at, length] of wipes) tiff.fill(0, at, at + length);
  return true;
}

/**
 * Scrub the head of a JPEG. Returns the new head (possibly shorter, when an
 * XMP segment was dropped), or null when there is nothing to change or
 * anything looks unfamiliar. `head` must start at the file's first byte.
 */
export function scrubJpegGpsHead(head: Uint8Array): Uint8Array | null {
  try {
    if (head.length < 4 || head[0] !== 0xff || head[1] !== 0xd8) return null;
    const out = new Uint8Array(head);
    const drop: Array<[number, number]> = [];
    let changed = false;
    let exifDone = false;
    let at = 2;
    while (at + 4 <= out.length) {
      if (out[at] !== 0xff) return null;
      const marker = out[at + 1];
      if (marker === 0xff) { at += 1; continue; } // fill byte
      if (marker === 0xda || marker === 0xd9) break; // image data or end: stop
      if (marker >= 0xd0 && marker <= 0xd7) { at += 2; continue; }
      const length = (out[at + 2] << 8) | out[at + 3];
      if (length < 2) return null;
      const segmentEnd = at + 2 + length;
      if (segmentEnd > out.length) break; // runs past the head we were given
      if (marker === 0xe1) {
        const body = at + 4;
        if (!exifDone && ascii(out, body, 6) === 'Exif\0\0') {
          exifDone = true;
          const tiff = out.subarray(body + 6, segmentEnd);
          if (scrubTiff(tiff)) changed = true;
        } else if (ascii(out, body, 29) === 'http://ns.adobe.com/xap/1.0/\0'
          && ascii(out, body, segmentEnd - body).includes('GPS')) {
          drop.push([at, segmentEnd]);
          changed = true;
        }
      }
      at = segmentEnd;
    }
    if (!changed) return null;
    if (drop.length === 0) return out;
    const parts: Uint8Array[] = [];
    let from = 0;
    for (const [start, end] of drop) {
      parts.push(out.subarray(from, start));
      from = end;
    }
    parts.push(out.subarray(from));
    const total = parts.reduce((sum, part) => sum + part.length, 0);
    const joined = new Uint8Array(total);
    let offset = 0;
    for (const part of parts) {
      joined.set(part, offset);
      offset += part.length;
    }
    return joined;
  } catch {
    return null;
  }
}

/** How much of the file the scrub reads: EXIF and XMP sit before the image data. */
export const JPEG_SCRUB_HEAD_BYTES = 256 * 1024;
